import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ChartLine, ChevronLeft, Clock } from "lucide-react";
import { DatePicker } from "antd";

import useApiMethods from "../../lib/useApiMethods";
import { ATTENDANCE_LOG_CHART } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";

const SEGMENT_STYLES = {
    late: "bg-orange-500 dark:bg-orange-600",
    ontime: "bg-emerald-400 dark:bg-emerald-600",
    productive: "bg-sidebar-primary",
    break: "bg-amber-400 dark:bg-amber-500",
    overtime: "bg-violet-500 dark:bg-violet-600",
    other: "bg-gray-400 dark:bg-gray-600",
};

const LEGEND = [
    { key: "late", label: "Late" },
    { key: "ontime", label: "On time" },
    { key: "productive", label: "Productive" },
    { key: "break", label: "Break" },
    { key: "overtime", label: "Overtime" },
];

const classifySegment = (status) => {
    const text = String(status || "").toLowerCase();
    if (text === "ontime") return "ontime";
    if (text === "productive") return "productive";
    if (text.includes("break")) return "break";
    if (text.includes("overtime")) return "overtime";
    if (text.includes("late")) return "late";
    return "other";
};

const parseDateTime = (value) => {
    if (!value || value === "None") return null;
    const cleaned = String(value).replace("T", " ").substring(0, 19);
    const parsed = dayjs(cleaned, "YYYY-MM-DD HH:mm:ss", true);
    return parsed.isValid() ? parsed : null;
};

const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
};

const buildDayTimeline = (dayRecord) => {
    const segments = dayRecord?.log || [];
    if (!segments.length) return null;

    const date = dayRecord.date;
    let windowStart = parseDateTime(dayRecord.shift_check_in) || parseDateTime(`${date} 09:00:00`);
    let windowEnd = parseDateTime(dayRecord.shift_check_out) || parseDateTime(`${date} 18:00:00`);

    segments.forEach((seg) => {
        const from = parseDateTime(seg.from_time);
        const to = parseDateTime(seg.to_time);
        if (from && (!windowStart || from.isBefore(windowStart))) windowStart = from;
        if (to && (!windowEnd || to.isAfter(windowEnd))) windowEnd = to;
    });

    if (!windowStart || !windowEnd) return null;

    const totalMinutes = Math.max(windowEnd.diff(windowStart, "minute"), 1);

    return {
        windowStart,
        windowEnd,
        totalMinutes,
        bars: segments
            .map((seg) => {
                const from = parseDateTime(seg.from_time);
                const to = parseDateTime(seg.to_time);
                if (!from || !to || !to.isAfter(from)) return null;
                const left = (from.diff(windowStart, "minute") / totalMinutes) * 100;
                const width = Math.max((to.diff(from, "minute") / totalMinutes) * 100, 1.5);
                return {
                    ...seg,
                    type: classifySegment(seg.status),
                    left,
                    width,
                    duration: to.diff(from, "minute"),
                };
            })
            .filter(Boolean),
    };
};

const AttendanceLogChart = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fetch } = useApiMethods();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const employeeId = location.state?.employeeId;
    const [selectedMonth, setSelectedMonth] = useState(dayjs());
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState(null);

    const fromDate = selectedMonth.startOf("month").format("YYYY-MM-DD");
    const toDate = selectedMonth.endOf("month").format("YYYY-MM-DD");

    useEffect(() => {
        if (!employeeId) return;

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const response = await fetchRef.current(ATTENDANCE_LOG_CHART, {
                    id: employeeId,
                    from_date: fromDate,
                    to_date: toDate,
                });
                if (cancelled) return;
                if (response?.status === "success") {
                    setChartData(response.data);
                } else {
                    setChartData(null);
                    ToastMsg("error", response?.message || "Failed to load attendance chart");
                }
            } catch (error) {
                if (!cancelled) {
                    setChartData(null);
                    ToastMsg("error", error.message || "Failed to load attendance chart");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [employeeId, fromDate, toDate]);

    const employeeDetails = chartData?.employee_details;
    const days = chartData?.attendance || [];

    const empDisplay = useMemo(() => {
        if (!employeeDetails) {
            return {
                first_name: location.state?.employeeName || "Employee",
                id: employeeId || "-",
                designation_id: { designation_name: "" },
            };
        }
        return {
            title: employeeDetails.title,
            first_name: employeeDetails.employee_name,
            id: employeeDetails.employee_id,
            profile: employeeDetails.profile,
            designation_id: {
                designation_name: employeeDetails.designation?.name || "",
            },
        };
    }, [employeeDetails, employeeId, location.state?.employeeName]);

    const summary = useMemo(() => {
        let present = 0;
        let late = 0;
        let absent = 0;
        days.forEach((day) => {
            const arrival = String(day.arrival || "").toLowerCase();
            if (arrival.includes("late")) late += 1;
            else if (arrival === "absent") absent += 1;
            else if (day.log?.length) present += 1;
        });
        return { total: days.length, present, late, absent };
    }, [days]);

    if (!employeeId) {
        return (
            <ContainerCard>
                <div className="p-8 text-center space-y-4">
                    <ChartLine className="w-10 h-10 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Select an employee from Attendance Log to view their chart.
                    </p>
                    <Button onClick={() => navigate("/attendance/attendancelog")}>
                        Go to Attendance Log
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/attendance/attendancelog")}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <ModuleTitleCard
                        Icon={ChartLine}
                        title="Attendance Log Chart"
                        subtitle={`${selectedMonth.format("MMMM YYYY")} · daily timeline`}
                        totalRecords={days.length}
                    />
                </div>
                <DatePicker
                    picker="month"
                    value={selectedMonth}
                    onChange={(value) => value && setSelectedMonth(value)}
                    allowClear={false}
                    format="MMMM YYYY"
                />
            </div>

            <div className="mb-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40">
                <EmployeeDetails details={empDisplay} size="sm" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[
                    { label: "Days in range", value: summary.total },
                    { label: "With logs", value: summary.present },
                    { label: "Late arrivals", value: summary.late, tone: "text-orange-600" },
                    { label: "Absent", value: summary.absent, tone: "text-rose-600" },
                ].map((item) => (
                    <div
                        key={item.label}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-center"
                    >
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                        <p className={cn("text-xl font-bold mt-1", item.tone || "text-gray-900 dark:text-white")}>
                            {item.value}
                        </p>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-3 mb-4">
                {LEGEND.map((item) => (
                    <span key={item.key} className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                        <span className={cn("w-3 h-3 rounded-sm", SEGMENT_STYLES[item.key])} />
                        {item.label}
                    </span>
                ))}
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
                </div>
            ) : days.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
                    <Clock className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No attendance records for this month.</p>
                </div>
            ) : (
                <div className="space-y-3 max-h-[calc(100vh-420px)] overflow-auto pr-1">
                    {days.map((day) => {
                        const timeline = buildDayTimeline(day);
                        return (
                            <div
                                key={day.date}
                                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {dayjs(day.date).format("ddd, DD MMM YYYY")}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                            {day.arrival || "No punch data"}
                                        </p>
                                    </div>
                                    {day.working_hrs && (
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                            Work {String(day.working_hrs)}
                                        </span>
                                    )}
                                </div>

                                {timeline ? (
                                    <>
                                        <div className="relative h-8 rounded-md bg-gray-100 dark:bg-gray-900/60 overflow-hidden">
                                            {timeline.bars.map((bar, idx) => (
                                                <div
                                                    key={`${day.date}-${idx}`}
                                                    className={cn(
                                                        "absolute top-1 bottom-1 rounded-sm opacity-90 hover:opacity-100 transition-opacity",
                                                        SEGMENT_STYLES[bar.type] || SEGMENT_STYLES.other,
                                                    )}
                                                    style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                                                    title={`${bar.status} (${formatDuration(bar.duration)})`}
                                                />
                                            ))}
                                        </div>
                                        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                                            <span>{timeline.windowStart.format("hh:mm A")}</span>
                                            <span>{timeline.windowEnd.format("hh:mm A")}</span>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                        No timeline segments for this day.
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </ContainerCard>
    );
};

export default AttendanceLogChart;
